import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Card, Input, Button } from "../components";
import { useAuth } from "../hooks/useAuth";
import { apiPost } from "../lib/api";
import { PUBLIC_CONSENT_VERSION } from "../../../../src/shared/contracts/public-consent.js";

export default function WaitlistLanding() {
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/fleet" replace />;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!acceptedTerms) {
      setError("Please acknowledge the early access terms and privacy notice.");
      return;
    }
    setLoading(true);
    try {
      await apiPost("/api/waitlist", {
        email,
        source: "landing_page",
        acceptedTerms: true,
        termsVersion: PUBLIC_CONSENT_VERSION,
      });
      setSubmitted(true);
      setEmail("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit waitlist request");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--c-surface-2)",
        padding: "1rem",
      }}
    >
      <div style={{ width: "100%", maxWidth: 520 }}>
        <Card>
          <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "0.75rem",
                fontSize: "0.65rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                padding: "4px 8px",
                borderRadius: 999,
                border: "1px solid var(--c-border)",
                color: "var(--c-text-faint)",
                backgroundColor: "var(--c-surface)",
              }}
            >
              Agent Me
            </div>
            <h1
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontSize: "2rem",
                fontWeight: 400,
                color: "var(--c-text)",
                marginBottom: "0.4rem",
              }}
            >
              Join the Waitlist
            </h1>
            <p style={{ color: "var(--c-text-secondary)", fontSize: "0.95rem" }}>
              Request access, then Adam approval unlocks your account setup and Hank provisioning.
            </p>
          </div>

          {submitted ? (
            <div style={{ textAlign: "center", color: "var(--c-text-secondary)" }}>
              <p style={{ marginBottom: "0.75rem" }}>
                You're on the list. We'll email you after approval.
              </p>
              <Link
                to="/register"
                style={{ color: "var(--c-primary)", textDecoration: "underline" }}
              >
                Create your account after approval
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <label
                style={{
                  display: "flex",
                  gap: "0.6rem",
                  alignItems: "flex-start",
                  marginTop: "0.9rem",
                  color: "var(--c-text-secondary)",
                  fontSize: "0.82rem",
                  lineHeight: 1.45,
                }}
              >
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  style={{ marginTop: "0.18rem" }}
                  required
                />
                <span>
                  I agree to receive Agent Me waitlist and account setup emails, and I acknowledge
                  the{" "}
                  <Link to="/privacy" style={{ color: "var(--c-primary)" }}>
                    Privacy Notice
                  </Link>{" "}
                  and{" "}
                  <Link to="/terms" style={{ color: "var(--c-primary)" }}>
                    Terms of Use
                  </Link>
                  .
                </span>
              </label>
              {error ? (
                <p style={{ marginTop: "0.75rem", color: "var(--c-danger)" }} role="alert">
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                variant="accent"
                className="w-full"
                style={{ marginTop: "1rem" }}
                disabled={loading || !email.trim() || !acceptedTerms}
              >
                {loading ? "Submitting..." : "Request Access"}
              </Button>
            </form>
          )}
          <p style={{ color: "var(--c-text-faint)", fontSize: "0.8rem", marginTop: "1rem" }}>
            Already approved? <Link to="/register">Create your account</Link> or{" "}
            <Link to="/login">sign in</Link>.
          </p>
        </Card>
      </div>
    </div>
  );
}
